import { ReactCropperElement } from "react-cropper";
import { ClickPosition, CropState } from "./types";
import { MIN_CROP_SIZE, DRAG_TOLERANCE, MIN_DRAG_SIZE } from "./constants";

/**
 * Get crop coordinates in original image dimensions
 */
export const getCurrentCropInOriginalCoordinates = (
  cropperRef: React.RefObject<ReactCropperElement>,
): [number, number, number, number] | null => {
  const cropper = cropperRef.current?.cropper;
  if (!cropper) return null;

  const box = cropper.getCropBoxData();
  const imageData = cropper.getImageData();
  const canvasData = cropper.getCanvasData();

  const scaleX = canvasData.width / imageData.naturalWidth;
  const scaleY = canvasData.height / imageData.naturalHeight;

  const originalX = (box.left - canvasData.left) / scaleX;
  const originalY = (box.top - canvasData.top) / scaleY;
  const originalWidth = box.width / scaleX;
  const originalHeight = box.height / scaleY;

  return [
    Math.round(originalX),
    Math.round(originalY),
    Math.round(originalX + originalWidth),
    Math.round(originalY + originalHeight),
  ];
};

/**
 * Extract click position from cropper event
 */
export const extractClickPositionFromEvent = (
  e: any,
  cropperRef: React.RefObject<ReactCropperElement>,
): ClickPosition | null => {
  if (!e.detail || !e.detail.originalEvent) return null;

  const originalEvent = e.detail.originalEvent;
  if (!originalEvent.clientX || !originalEvent.clientY) return null;

  const cropper = cropperRef.current?.cropper;
  if (!cropper) return null;

  const container = cropper.getContainerData();
  let rect;

  const cropperContainer = (cropper as any).element?.parentElement;
  if (cropperContainer) {
    rect = cropperContainer.getBoundingClientRect();
  } else {
    const cropperElements =
      document.getElementsByClassName("cropper-container");
    if (cropperElements.length > 0) {
      rect = cropperElements[0].getBoundingClientRect();
    }
  }

  if (!rect) return null;

  const rawClickX = originalEvent.clientX - rect.left;
  const rawClickY = originalEvent.clientY - rect.top;
  const containerOffsetX = (rect.width - container.width) / 2;
  const containerOffsetY = (rect.height - container.height) / 2;
  const containerClickX = rawClickX - containerOffsetX;
  const containerClickY = rawClickY - containerOffsetY;

  return {
    x: containerClickX,
    y: containerClickY,
  };
};

/**
 * Detect if user clicked without dragging
 */
export const isClickWithoutDrag = (
  initialCropState: CropState | null,
  currentBox: any,
): boolean => {
  if (!currentBox) return true;

  const { width, height, left, top } = currentBox;

  if (!initialCropState) {
    return !width || !height || width < MIN_DRAG_SIZE || height < MIN_DRAG_SIZE;
  }

  const positionChanged =
    Math.abs(left - initialCropState.left) > DRAG_TOLERANCE ||
    Math.abs(top - initialCropState.top) > DRAG_TOLERANCE;
  const sizeChanged =
    Math.abs(width - initialCropState.width) > DRAG_TOLERANCE ||
    Math.abs(height - initialCropState.height) > DRAG_TOLERANCE;

  return !positionChanged && !sizeChanged;
};

/**
 * Calculate crop position centered on click point, constrained to canvas bounds
 */
export const calculateCropPosition = (
  clickPos: ClickPosition,
  canvas: any,
  width: number = MIN_CROP_SIZE,
  height: number = MIN_CROP_SIZE,
): { left: number; top: number } => {
  // Check if click is within image bounds
  const isClickInImage =
    clickPos.x >= canvas.left &&
    clickPos.x <= canvas.left + canvas.width &&
    clickPos.y >= canvas.top &&
    clickPos.y <= canvas.top + canvas.height;

  if (isClickInImage) {
    // Center the crop box on the click position, but constrain to canvas bounds
    const left = Math.max(
      canvas.left,
      Math.min(clickPos.x - width / 2, canvas.left + canvas.width - width),
    );
    const top = Math.max(
      canvas.top,
      Math.min(clickPos.y - height / 2, canvas.top + canvas.height - height),
    );
    return { left, top };
  } else {
    // Click outside image, center in canvas
    return {
      left: canvas.left + (canvas.width - width) / 2,
      top: canvas.top + (canvas.height - height) / 2,
    };
  }
};

/**
 * Apply minimum size constraints to crop box
 */
export const applyMinimumSizeConstraints = (
  box: any,
  minSize: number = MIN_CROP_SIZE,
): { width: number; height: number; left: number; top: number } => {
  let { width, height, left, top } = box;

  if (width < minSize) {
    const delta = minSize - width;
    width = minSize;
    left = Math.max(0, left - delta / 2);
  }
  if (height < minSize) {
    const delta = minSize - height;
    height = minSize;
    top = Math.max(0, top - delta / 2);
  }

  return { width, height, left, top };
};

/**
 * Set up bounding box crop on ready
 */
export const setupBoundingBoxCrop = (
  cropper: any,
  boundingBox: [number, number, number, number],
): ClickPosition => {
  const [x, y, x1, y1] = boundingBox;
  const boundingWidth = x1 - x;
  const boundingHeight = y1 - y;

  const imageData = cropper.getImageData();
  const canvasData = cropper.getCanvasData();

  const scaleX = canvasData.width / imageData.naturalWidth;
  const scaleY = canvasData.height / imageData.naturalHeight;

  const displayX = x * scaleX;
  const displayY = y * scaleY;
  const displayWidth = boundingWidth * scaleX;
  const displayHeight = boundingHeight * scaleY;

  cropper.setCropBoxData({
    left: canvasData.left + displayX,
    top: canvasData.top + displayY,
    width: displayWidth,
    height: displayHeight,
  });

  return {
    x: canvasData.left + displayX + displayWidth / 2,
    y: canvasData.top + displayY + displayHeight / 2,
  };
};
